/*
 * assets/ce-header-search.js — <ce-search>
 *
 * Die Suche der Kopfzeile klappt auf, statt eine Seite zu laden
 * (snippets/header-search.liquid).
 *
 * Was OHNE dieses Skript schon funktioniert — und das ist wieder der Punkt:
 * Im Markup steht ein vollstaendiges GET-Formular auf /search mit `name="q"`,
 * die Lupe ist sein Absenden-Knopf, und die noscript-Regeln in
 * sections/header.liquid halten das Feld offen. Wer ohne JavaScript kommt,
 * tippt und schickt ab wie eh und je.
 *
 * Was dieses Skript hinzufuegt:
 *
 *   1. `aria-expanded` an der Lupe. Das Attribut im Markup zu setzen waere
 *      gelogen, solange niemand aufklappen kann — und ohne Skript ist das
 *      Feld ja offen.
 *   2. Den ersten Klick. Geschlossen faengt der Knopf sein eigenes Absenden
 *      ab und klappt auf; offen ist er wieder der Absenden-Knopf, der er im
 *      Markup ist. Ein Knopf, zwei Rollen — kein zweiter, der geschlossen
 *      nichts zu tun haette.
 *   3. Den Rueckweg: Escape, der Schliessen-Knopf, ein Klick daneben, und
 *      ein Fokus, der das Element verlaesst.
 *
 * Der Rueckweg schliesst NUR bei leerem Feld. Ein getippter Begriff ist
 * Arbeit des Nutzers — sie wegzuklappen, weil er kurz woandershin fasst,
 * waere Datenverlust. Escape und der Schliessen-Knopf sind ausdrueckliche
 * Abbrueche und raeumen deshalb auch ein volles Feld ab.
 */

const MOBILE = matchMedia('(max-width: 989px)');

class CESearch extends window.CE.Element {
  setup(signal) {
    this.form = this.querySelector('.ce-search__form');
    this.input = this.querySelector('.ce-search__input');
    this.toggle = this.querySelector('.ce-search__toggle');
    if (!this.form || !this.input || !this.toggle) return;

    this.applyAria();
    MOBILE.addEventListener('change', () => this.applyAria(), { signal });
    this.setLabel(false);

    this.toggle.addEventListener('click', (event) => {
      if (this.isOpen) return; // offen: der Knopf ist wieder Absenden-Knopf
      event.preventDefault();
      if (this.openMenuSearch()) return;
      this.setOpen(true);
    }, { signal });

    // Leer abgeschickt landete man auf /search?q= — einer Ergebnisseite ohne
    // Frage. Der Klick bleibt dann einfach folgenlos, der Fokus im Feld.
    this.form.addEventListener('submit', (event) => {
      if (this.input.value.trim() === '') {
        event.preventDefault();
        this.input.focus();
      }
    }, { signal });

    this.querySelector('.ce-search__close')?.addEventListener('click', () => {
      this.setOpen(false);
    }, { signal });

    // Am Element, nicht am document: Escape gehoert hier nur der Suche,
    // solange der Fokus in ihr liegt.
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) this.setOpen(false);
    }, { signal });

    this.addEventListener('focusout', (event) => {
      if (!this.isOpen || this.input.value !== '') return;
      if (!this.contains(event.relatedTarget)) this.setOpen(false);
    }, { signal });

    // pointerdown statt click: schliesst schon beim Aufsetzen des Fingers,
    // also bevor das darunterliegende Ziel seinen eigenen Klick bekommt.
    document.addEventListener('pointerdown', (event) => {
      if (!this.isOpen || this.input.value !== '') return;
      if (!this.contains(event.target)) this.setOpen(false);
    }, { signal });
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  /**
   * Unter 990px klappt die Lupe nichts in der Kapsel aus, sondern oeffnet das
   * Mobil-Menue mit dem Fokus in dessen Suchfeld (Mobile Review, Etappe 2):
   * neben Logo, Warenkorb und Burger ist fuer ein Feld kein Platz, und im
   * Menue stehen unter der Suche gleich die Kategorien. Die Lupe ist dabei
   * der Ausloeser — der Fokus kehrt beim Schliessen zu ihr zurueck.
   *
   * Gibt false zurueck, wenn es kein Menue gibt (oder die Breite >= 990px
   * ist); dann klappt die Suche wie bisher an Ort und Stelle aus.
   */
  openMenuSearch() {
    if (!MOBILE.matches) return false;
    const drawer = this.menuDrawer();
    if (!drawer || typeof drawer.openSearch !== 'function') return false;
    return drawer.openSearch(this.toggle);
  }

  /** Das Mobil-Menue mit eigenem Suchfeld, sonst null. */
  menuDrawer() {
    const drawer = this.closest('ce-header')?.querySelector('ce-nav-drawer');
    return drawer?.id && drawer.querySelector('[data-menu-search]') ? drawer : null;
  }

  /**
   * Was die Lupe Screenreadern sagt, folgt dem, was sie tut. Unter 990px
   * oeffnet sie den Menue-Dialog: aria-controls zeigt auf das Menue,
   * aria-haspopup="dialog", aria-expanded haelt ce-nav-drawer.js beim
   * Oeffnen und Schliessen nach (es setzt es an jedem Element, das auf das
   * Menue zeigt). Ab 990px klappt sie das Feld in der Kapsel aus, wie bisher.
   * Umgeschaltet wird per matchMedia, nicht im Liquid: dieselbe Seite wechselt
   * beim Drehen des Geraets ueber die Grenze.
   */
  applyAria() {
    const drawer = MOBILE.matches ? this.menuDrawer() : null;
    if (drawer) {
      this.toggle.setAttribute('aria-controls', drawer.id);
      this.toggle.setAttribute('aria-haspopup', 'dialog');
      this.toggle.setAttribute('aria-expanded', String(drawer.classList.contains('is-open')));
    } else {
      this.toggle.setAttribute('aria-controls', this.input.id);
      this.toggle.removeAttribute('aria-haspopup');
      this.toggle.setAttribute('aria-expanded', String(this.isOpen));
    }
  }

  /**
   * Geschlossen heisst die Lupe „Suche oeffnen", offen „Suchen" — das ist
   * dann keine Beschreibung mehr, sondern die Zusage, was der Klick tut.
   */
  setLabel(open) {
    const label = open ? this.toggle.dataset.labelSubmit : this.toggle.dataset.labelOpen;
    if (label) this.toggle.setAttribute('aria-label', label);
  }

  setOpen(open) {
    if (this.isOpen === open) return;

    this.classList.toggle('is-open', open);
    this.toggle.setAttribute('aria-expanded', String(open));
    this.setLabel(open);

    if (open) {
      this.input.focus();
      return;
    }

    this.input.value = '';

    // Der Fokus darf nicht im zugeklappten Feld stehenbleiben — dort waere er
    // fuer den Nutzer verschwunden. Lag er draussen (Klick daneben), bleibt
    // er auch draussen: ihn zurueckzuholen waere ein Sprung, den niemand
    // angefordert hat.
    if (this.contains(document.activeElement)) this.toggle.focus();
  }
}

customElements.define('ce-search', CESearch);
